/**
 * Erase and Redraw — 3-tap flow per spec.
 *  Tap 1: open the sheet (from Next Play card)
 *  Tap 2: confirm cancellation
 *  Tap 3: (implicit) the sheet auto-dismisses 1.5s after confirm
 *
 * The AI draft is pre-loaded on `isOpen=true` so the coach never waits.
 * The confirm button shows the exact family count: "Erase play · notify N families".
 */
import { useEffect, useRef, useState } from "react";
import { Sparkles, Zap } from "lucide-react";
import { toast } from "sonner";

import {
  BottomSheet, ChalkActionButton, SectionTitle,
} from "./primitives";
import { EraseDoneStep } from "./eraseRedraw/EraseDoneStep";
import { api } from "../../lib/chalkApi";

export default function EraseRedrawSheet({ isOpen, event, sport, onDismiss, onCancelled }) {
  const [draft, setDraft] = useState("");
  const [familyCount, setFamilyCount] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState("review"); // 'review' | 'done'
  const [maxDeliveryMs, setMaxDeliveryMs] = useState(null);
  const draftLoadedRef = useRef(null);

  // Pre-load AI draft + family count when the sheet opens.
  useEffect(() => {
    // Guard: once we've moved to the success state, ignore further prop changes.
    if (step === "done") return;
    if (!isOpen || !event) return;
    setStep("review");
    setSubmitting(false);
    setMaxDeliveryMs(null);

    // Family count: best-effort from the event team's roster
    api.listRoster(event.team_id).then((data) => {
      const guardians = new Set();
      for (const a of data.athletes || []) {
        for (const g of a.guardians || []) guardians.add(g.user_id);
      }
      setFamilyCount(guardians.size);
    }).catch(() => setFamilyCount(null));

    // AI pre-draft — only fetch once per event open
    if (draftLoadedRef.current !== event.id) {
      draftLoadedRef.current = event.id;
      setDraft("");
      api.aiEraseDraft(event.title, "Field is unsafe").then((d) => {
        if (d?.draft) setDraft(d.draft);
      }).catch(() => {
        setDraft(`Hey families — we're calling ${event.title}. Coach will follow up soon.`);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, event?.id]);

  async function confirm() {
    if (!event) return;
    setSubmitting(true);
    const started = performance.now();
    try {
      const res = await api.cancelEvent(event.id, draft || "Coach will update soon.");
      // Best-effort measurement: round-trip from confirm to API ack (server records
      // a more precise number including the queue-fanout).
      setMaxDeliveryMs(Math.round(performance.now() - started));
      setStep("done");
      onCancelled && onCancelled(event.id, { familyCount: res?.family_count ?? familyCount });
      // 1.5s auto-dismiss per spec. Reset to 'review' only AFTER dismiss so the
      // success UI stays visible the full 1.5s even if the parent removes the event.
      setTimeout(() => {
        onDismiss && onDismiss();
        setTimeout(() => {
          setStep("review");
          draftLoadedRef.current = null;
          setDraft("");
        }, 200);
      }, 1500);
    } catch (e) {
      toast.error(e.message || "Couldn't erase that play.");
      setSubmitting(false);
    }
  }

  const labelCount = familyCount == null ? "" : ` · notify ${familyCount} famil${familyCount === 1 ? "y" : "ies"}`;

  return (
    <BottomSheet isOpen={isOpen} title="Erase and Redraw" onDismiss={onDismiss}>
      {step === "review" && event && (
        <div data-testid="erase-sheet">
          <div className="chalk-card" style={{ borderLeft: "2px solid var(--chalk-red)" }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: "var(--chalk-board)" }}>{event.title}</div>
            <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 4 }}>
              {new Date(event.local_time).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} · {event.location || "TBD"}
            </div>
          </div>

          <SectionTitle label="Message to families" />
          <div style={{ position: "relative" }}>
            <textarea
              data-testid="erase-draft"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={4}
              placeholder={draft ? "" : "Drafting…"}
              className="chalk-input"
              style={{
                paddingLeft: 14, paddingTop: 12, paddingRight: 36,
                height: "auto", minHeight: 96, resize: "vertical", fontSize: 14,
                lineHeight: 1.4,
              }}
              aria-label="Cancellation message"
            />
            <span style={{ position: "absolute", top: 10, right: 10, color: "var(--chalk-green)" }} title="AI pre-draft">
              <Sparkles size={14} aria-hidden="true" />
            </span>
          </div>
          <div style={{ fontSize: 11, color: "var(--chalk-muted)", marginTop: 6 }}>
            Edit the draft if you like, or send as-is.
          </div>

          <div style={{ marginTop: 16 }}>
            <ChalkActionButton
              testId="erase-confirm"
              variant="danger"
              label={`Erase play${labelCount}`}
              icon={<Zap size={16} aria-hidden="true" />}
              onClick={confirm}
              loading={submitting}
            />
          </div>
          <button
            onClick={onDismiss}
            data-testid="erase-cancel"
            style={{ marginTop: 8, background: "transparent", border: "none", color: "var(--chalk-muted)", fontSize: 13, width: "100%", padding: 10, cursor: "pointer" }}
          >
            Keep the play on the board
          </button>
        </div>
      )}

      {step === "done" && <EraseDoneStep maxDeliveryMs={maxDeliveryMs} />}
    </BottomSheet>
  );
}
